import axios from 'axios';
import { Action } from 'redux';

import { IRequestAction } from 'redux/root';
import {
  publicURL,
  publicGQL,
  authenticatedURL,
  authenticatedGQL,
} from 'stubs';
import {
  ISwapStats,
  ITxnCollection,
  IItemCollection,
  IItemEdge,
  ISearchItemsInput,
  ICartItemCollection,
  IDeliveryDetailsInput,
  ITxnEdge,
} from 'stubs/graphql/swap';
import { showLoader, hideLoader } from 'redux/loader/actions';
import { ISearchResults } from './reducer';
import { meRequest } from 'redux/account/actions';

// --------------
// Actions
// --------------
export enum SWAP_ACTIONS {
  UPDATE_RETRIEVE_ALL_TXNS = 'SWAP_ACTIONS/UPDATE_RETRIEVE_ALL_TXNS',
  UPDATE_RETRIEVE_SWAP_STATS = 'SWAP_ACTIONS/UPDATE_RETRIEVE_SWAP_STATS',
  UPDATE_SEARCH_RESULTS = 'SWAP_ACTIONS/UPDATE_SEARCH_RESULTS',
  UPDATE_CART = 'SWAP_ACTIONS/UPDATE_CART',
  UPDATE_ITEM_DETAILS = 'SWAP_ACTIONS/UPDATE_ITEM_DETAILS',
}

// --------------
// Action types
// --------------
export interface IUpdateRetrieveAllTxns
  extends Action<SWAP_ACTIONS.UPDATE_RETRIEVE_ALL_TXNS> {
  txns: ITxnCollection;
}

export interface IUpdateRetrieveSwapStats
  extends Action<SWAP_ACTIONS.UPDATE_RETRIEVE_SWAP_STATS> {
  stats: ISwapStats;
}

export interface IUpdateSearchResults
  extends Action<SWAP_ACTIONS.UPDATE_SEARCH_RESULTS> {
  search: ISearchResults;
}

export interface IUpdateCart extends Action<SWAP_ACTIONS.UPDATE_CART> {
  cart: ICartItemCollection;
}

export interface IUpdateItemDetails
  extends Action<SWAP_ACTIONS.UPDATE_ITEM_DETAILS> {
  item: IItemEdge;
}

export type ISwapActions =
  | IUpdateRetrieveAllTxns
  | IUpdateRetrieveSwapStats
  | IUpdateSearchResults
  | IUpdateCart
  | IUpdateItemDetails;

// --------------
// Action creators
// --------------

export const retrieveAllTxnsRequest =
  (): IRequestAction => async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = authenticatedGQL.retrieveAllTxns;
      const variables = {};
      const res = await axios.post<any>(authenticatedURL, { query, variables });

      const txns: ITxnCollection = res.data.data.retrieveAllTxns;

      dispatch(updateRetrieveAllTxns(txns));
    } catch (e) {
      // Handled by interceptor
    } finally {
      dispatch(hideLoader());
    }
  };

export const updateRetrieveAllTxns = (
  txns: ITxnCollection
): IUpdateRetrieveAllTxns => ({
  type: SWAP_ACTIONS.UPDATE_RETRIEVE_ALL_TXNS,
  txns,
});

export const retrieveSwapStatsRequest =
  (): IRequestAction => async (dispatch) => {
    try {
      const query = authenticatedGQL.retrieveSwapStats;
      const variables = {};
      const res = await axios.post<any>(authenticatedURL, { query, variables });

      const stats: ISwapStats = res.data.data.retrieveSwapStats;

      dispatch(updateRetrieveSwapStats(stats));
    } catch (e) {
      // Handled by interceptor
    }
  };

export const updateRetrieveSwapStats = (
  stats: ISwapStats
): IUpdateRetrieveSwapStats => ({
  type: SWAP_ACTIONS.UPDATE_RETRIEVE_SWAP_STATS,
  stats,
});

export const searchItemsRequest =
  (
    input: ISearchItemsInput,
    pageNumber: number,
    pageSize: number
  ): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = publicGQL.searchItems;
      const variables = { input, pageNumber, pageSize };
      const res = await axios.post<any>(publicURL, { query, variables });

      const collection: IItemCollection = res.data.data.searchItems;
      const total: number = res.data.data.countItems;

      dispatch(
        updateSearchResults({
          total: total,
          pageSize: pageSize,
          pageNumber: pageNumber,
          collection: collection,
        })
      );
    } catch (e) {
      // Handled by interceptor
    } finally {
      dispatch(hideLoader());
    }
  };

export const updateSearchResults = (
  search: ISearchResults
): IUpdateSearchResults => ({
  type: SWAP_ACTIONS.UPDATE_SEARCH_RESULTS,
  search,
});

export const retrieveItemBySKURequest =
  (sku: string): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = publicGQL.retrieveItemBySKU;
      const variables = { sku };
      const res = await axios.post<any>(publicURL, { query, variables });

      const item: IItemEdge = res.data.data.retrieveItemBySKU;

      dispatch(updateItemDetails(item));
      return item;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      dispatch(hideLoader());
    }
  };

export const updateItemDetails = (item: IItemEdge): IUpdateItemDetails => ({
  type: SWAP_ACTIONS.UPDATE_ITEM_DETAILS,
  item,
});

export const retrieveCartRequest = (): IRequestAction => async (dispatch) => {
  try {
    dispatch(showLoader());
    const query = authenticatedGQL.retrieveCart;
    const variables = {};
    const res = await axios.post<any>(authenticatedURL, { query, variables });

    const cart: ICartItemCollection = res.data.data.retrieveCart;

    dispatch(updateCart(cart));
  } catch (e) {
    // Handled by interceptor
  } finally {
    dispatch(hideLoader());
  }
};

export const addToCartRequest =
  (itemID: number): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = authenticatedGQL.addToCart;
      const variables = { itemID };
      await axios.post<any>(authenticatedURL, { query, variables });

      return null;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      dispatch(hideLoader());
    }
  };

export const removeFromCartRequest =
  (itemID: number): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = authenticatedGQL.removeFromCart;
      const variables = { itemID };
      const res = await axios.post<any>(authenticatedURL, { query, variables });

      const cart: ICartItemCollection = res.data.data.removeFromCart;

      dispatch(updateCart(cart));
      return null;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      dispatch(hideLoader());
    }
  };

export const updateCart = (cart: ICartItemCollection): IUpdateCart => ({
  type: SWAP_ACTIONS.UPDATE_CART,
  cart,
});

export const verifyCartRequest = (): IRequestAction => async (dispatch) => {
  try {
    dispatch(showLoader());
    const query = authenticatedGQL.verifyCart;
    const variables = {};
    await axios.post<any>(authenticatedURL, { query, variables });

    return null;
  } catch (e) {
    return Promise.reject(e);
  } finally {
    dispatch(hideLoader());
  }
};

export const checkoutRequest =
  (
    expectedTotalPoints: number,
    expectedTotalCents: number,
    delivery: IDeliveryDetailsInput,
    externalTxnID?: string
  ): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = authenticatedGQL.checkout;
      const variables = {
        expectedTotalPoints,
        expectedTotalCents,
        externalTxnID,
        delivery,
      };
      await axios.post<any>(authenticatedURL, { query, variables });

      // Update member information
      dispatch(meRequest());
      return null;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      dispatch(hideLoader());
    }
  };

export const retrieveTxnRequest =
  (id: number): IRequestAction =>
  async (dispatch) => {
    try {
      dispatch(showLoader());
      const query = authenticatedGQL.retrieveTxn;
      const variables = { id };
      const res = await axios.post<any>(authenticatedURL, { query, variables });

      const txn: ITxnEdge = res.data.data.retrieveTxn;

      return txn;
    } catch (e) {
      return Promise.reject(e);
    } finally {
      dispatch(hideLoader());
    }
  };
